// src/services/infoCentre/session_formation_service.js
import api from '../api';

const BASE = 'sessions/';

/**
 * Récupère toutes les sessions actives (non terminées)
 * GET /api/sessions/
 */
export const getSessions = async () => {
  const response = await api.get(BASE);
  return response.data;
};

/**
 * Récupère toutes les sessions terminées (archivées automatiquement)
 * GET /api/sessions/terminees/
 */
export const getSessionsTerminees = async () => {
  const response = await api.get(`${BASE}terminees/`);
  return response.data;
};

/**
 * Crée une nouvelle session de formation
 * POST /api/sessions/
 * @param {Object} data - données de la session
 */
export const ajouterSession = async (data) => {
  const response = await api.post(BASE, data);
  return response.data;
};

/**
 * Modifie une session existante
 * PUT /api/sessions/:id/
 * @param {number} id
 * @param {Object} data
 */
export const modifierSession = async (id, data) => {
  const response = await api.put(`${BASE}${id}/`, data);
  return response.data;
};

/**
 * Modifie partiellement une session (n'écrase pas les champs non fournis).
 * PATCH /api/sessions/:id/
 * @param {number} id
 * @param {Object} data
 */
export const patchSession = async (id, data) => {
  const response = await api.patch(`${BASE}${id}/`, data);
  return response.data;
};

/**
 * Supprime une session
 * DELETE /api/sessions/:id/
 * @param {number} id
 */
export const supprimerSession = async (id, force = false) => {
  // Une session qui a des inscrits répond 409 tant que la suppression n'est
  // pas confirmée explicitement (force=1 : les inscriptions sont supprimées).
  const response = await api.delete(`${BASE}${id}/`, { params: force ? { force: 1 } : {} });
  return response.data;
};

/**
 * Récupère une session par son ID
 * GET /api/sessions/:id/
 * @param {number} id
 */
export const getSessionById = async (id) => {
  const response = await api.get(`${BASE}${id}/`);
  return response.data;
};


/**
 * Récupère les sessions disponibles (non terminées) pour une formation
 * @param {number} formationId
 */
export const getSessionsDisponiblesForFormation = async (formationId) => {
    const response = await api.get(`${BASE}disponibles/formation/${formationId}/`);
    return response.data;
};

/**
 * Récupère les sessions disponibles pour plusieurs formations en une requête
 * @param {number[]} formationIds
 */
export const getSessionsDisponiblesBatch = async (formationIds) => {
    const response = await api.post(`${BASE}disponibles/batch/`, { formation_ids: formationIds });
    return response.data;
};

// NOUVEAU : Reporter une session (vider les dates)
export const reporterSession = async (id) => {
  const response = await api.post(`${BASE}${id}/reporter/`);
  return response.data;
};

// NOUVEAU : Reprogrammer une session (définir de nouvelles dates)
export const reprogrammerSession = async (id, data) => {
  const response = await api.put(`${BASE}${id}/reprogrammer/`, data);
  return response.data;
};

export const getNouvellesSessions = () => api.get('sessions/nouvelles/');
export const markSessionsAsRead = () => api.post('sessions/marquer-lu/');

/**
 * Supprime des sessions. Celles qui ont des inscrits (le serveur répond 409,
 * error = "session_has_inscriptions") ne sont supprimées qu'après UNE
 * confirmation explicite, qui les désinscrit. Avant, ce 409 s'affichait comme
 * « Erreur lors de la suppression ».
 *
 * Renvoie { supprimees, echecs } ; `supprimer` et `confirmer` sont injectables
 * (tests).
 */
export const supprimerSessionsAvecConfirmation = async (
  ids, { supprimer = supprimerSession, confirmer = (q) => window.confirm(q) } = {}
) => {
  const resultats = await Promise.allSettled(ids.map((id) => supprimer(id)));
  const avecInscrits = [];
  const echecs = [];
  resultats.forEach((r, i) => {
    if (r.status === "fulfilled") return;
    const rep = r.reason?.response;
    if (rep?.status === 409 && rep.data?.error === "session_has_inscriptions") {
      avecInscrits.push({ id: ids[i], inscrits: rep.data.inscriptions_count || 0 });
    } else {
      echecs.push(rep?.data?.detail || rep?.data?.message || "Erreur serveur.");
    }
  });
  let supprimees = resultats.filter((r) => r.status === "fulfilled").length;

  if (avecInscrits.length) {
    const total = avecInscrits.reduce((t, s) => t + s.inscrits, 0);
    const question = ids.length === 1
      ? `Cette session compte ${total} apprenant(s) inscrit(s). Les désinscrire et supprimer la session ?`
      : `${avecInscrits.length} session(s) comptent des apprenants inscrits (${total} au total). `
        + "Les désinscrire et supprimer ces sessions ?";
    if (confirmer(question)) {
      const forces = await Promise.allSettled(avecInscrits.map((s) => supprimer(s.id, true)));
      forces.forEach((r) => {
        if (r.status === "fulfilled") supprimees += 1;
        else echecs.push(r.reason?.response?.data?.detail || "Erreur serveur.");
      });
    }
  }
  return { supprimees, echecs };
};
